/**
 * GenericFilters Component
 *
 * Configuration-driven filter component that eliminates duplication across
 * CategoryFilters, CostCenterFilters, PaymentAccountFilters, and others.
 *
 * Usage:
 * ```tsx
 * const config: FilterConfig = {
 *   fields: [
 *     { key: 'type', type: 'select', label: 'Type', options: [...] },
 *     { key: 'status', type: 'select', label: 'Status', options: [...] }
 *   ],
 *   sortFields: [
 *     { key: 'name', label: 'Name' },
 *     { key: 'created_at', label: 'Created Date' }
 *   ]
 * };
 *
 * <GenericFilters filters={filters} onFiltersChange={setFilters} config={config} />
 * ```
 */

import React from 'react';
import { X, ArrowUpDown, RefreshCw } from 'lucide-react';
import { Button } from './button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';
import { Badge } from './badge';
import { Input } from './input';
import { Checkbox } from './checkbox';
import { Label } from './label';
import type {
  BaseFilters,
  FilterFieldConfig,
  FilterFieldType,
  GenericFiltersProps,
  SortOrder,
} from '@/types/filter';
import { cn } from '@/lib/utils';

export function GenericFilters<TFilters extends BaseFilters = BaseFilters>({
  filters,
  onFiltersChange,
  config,
  loading = false,
  onRefresh,
  className,
}: GenericFiltersProps<TFilters>) {
  const {
    fields,
    sortFields = [],
    defaultSortBy,
    defaultSortOrder = 'asc',
    formatFilterValue,
    gridColumns = 4,
  } = config;

  /**
   * Update a single filter field
   */
  const updateFilter = (key: string, value: unknown) => {
    onFiltersChange({
      ...filters,
      [key]: value,
    } as TFilters);
  };

  /**
   * Clear a single filter field
   */
  const clearFilter = (key: string) => {
    const newFilters = { ...filters };
    delete newFilters[key as keyof typeof newFilters];
    onFiltersChange(newFilters as TFilters);
  };

  /**
   * Clear all filters except sort
   */
  const clearAllFilters = () => {
    const defaultFilters: BaseFilters = {
      sortBy: defaultSortBy || sortFields[0]?.key,
      sortOrder: defaultSortOrder,
    };
    onFiltersChange(defaultFilters as TFilters);
  };

  /**
   * Calculate active filters (excluding sort fields)
   */
  const activeFilters = Object.entries(filters).filter(
    ([key, value]) =>
      key !== 'sortBy' &&
      key !== 'sortOrder' &&
      value !== undefined &&
      value !== null &&
      value !== '' &&
      (Array.isArray(value) ? value.length > 0 : true)
  );

  const hasActiveFilters = activeFilters.length > 0;
  const activeFilterCount = activeFilters.length;

  /**
   * Format filter value for display in badge
   */
  const getFilterDisplayValue = (key: string, value: unknown): string => {
    if (formatFilterValue) {
      return formatFilterValue(key, value);
    }

    const field = fields.find((f) => f.key === key);
    if (!field) return String(value);

    // For select/multiselect, find the matching option label
    if (field.type === 'select' || field.type === 'multiselect') {
      if (Array.isArray(value)) {
        return value
          .map((v) => field.options?.find((opt) => opt.value === v)?.label || String(v))
          .join(', ');
      }
      return field.options?.find((opt) => opt.value === value)?.label || String(value);
    }

    // For checkbox, show Yes/No
    if (field.type === 'checkbox') {
      return value ? 'Yes' : 'No';
    }

    return String(value);
  };

  /**
   * Render individual filter field based on type
   */
  const renderFilterField = (field: FilterFieldConfig) => {
    const value = filters[field.key as keyof typeof filters];
    const hasValue =
      value !== undefined &&
      value !== null &&
      value !== '' &&
      (Array.isArray(value) ? value.length > 0 : true);

    const gridSpanClass = field.gridSpan
      ? `md:col-span-${field.gridSpan}`
      : 'md:col-span-1';

    switch (field.type) {
      case 'select':
        return (
          <div key={field.key} className={cn('space-y-2', gridSpanClass)}>
            <label className="text-xs font-medium text-muted-foreground">
              {field.label}
            </label>
            <div className="flex items-center gap-2">
              <Select
                value={String(value || '__all__')}
                onValueChange={(val) =>
                  updateFilter(field.key, val === '__all__' ? undefined : val)
                }
              >
                <SelectTrigger className="h-8">
                  <SelectValue placeholder={field.placeholder || `All ${field.label}`} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">
                    {field.placeholder || `All ${field.label}`}
                  </SelectItem>
                  {field.options?.map((option) => (
                    <SelectItem key={String(option.value)} value={String(option.value)}>
                      <div className="flex items-center gap-2">
                        {option.icon && <option.icon className="h-3 w-3" />}
                        {option.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {field.clearable !== false && hasValue && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => clearFilter(field.key)}
                  className="h-8 w-8 p-0 shrink-0"
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
        );

      case 'search':
        return (
          <div key={field.key} className={cn('space-y-2', gridSpanClass)}>
            <label className="text-xs font-medium text-muted-foreground">
              {field.label}
            </label>
            <div className="flex items-center gap-2">
              <Input
                type="text"
                placeholder={field.placeholder || `Search ${field.label}...`}
                value={String(value || '')}
                onChange={(e) => updateFilter(field.key, e.target.value || undefined)}
                className="h-8"
              />
              {field.clearable !== false && hasValue && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => clearFilter(field.key)}
                  className="h-8 w-8 p-0 shrink-0"
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
        );

      case 'checkbox':
        return (
          <div key={field.key} className={cn('space-y-2', gridSpanClass)}>
            <div className="flex items-center gap-2 pt-6">
              <Checkbox
                id={field.key}
                checked={Boolean(value)}
                onCheckedChange={(checked) => updateFilter(field.key, checked)}
              />
              <Label
                htmlFor={field.key}
                className="text-xs font-medium cursor-pointer"
              >
                {field.label}
              </Label>
            </div>
          </div>
        );

      case 'multiselect':
        // Simplified multiselect - renders multiple checkboxes
        return (
          <div key={field.key} className={cn('space-y-2', gridSpanClass)}>
            <label className="text-xs font-medium text-muted-foreground">
              {field.label}
            </label>
            <div className="space-y-1">
              {field.options?.map((option) => {
                const values = Array.isArray(value) ? value : [];
                const isChecked = values.includes(option.value);
                return (
                  <div key={String(option.value)} className="flex items-center gap-2">
                    <Checkbox
                      id={`${field.key}-${option.value}`}
                      checked={isChecked}
                      onCheckedChange={(checked) => {
                        const newValues = checked
                          ? [...values, option.value]
                          : values.filter((v) => v !== option.value);
                        updateFilter(field.key, newValues);
                      }}
                    />
                    <Label
                      htmlFor={`${field.key}-${option.value}`}
                      className="text-xs cursor-pointer"
                    >
                      {option.label}
                    </Label>
                  </div>
                );
              })}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={cn('space-y-4 p-4 bg-muted/30 rounded-lg border', className)}>
      {/* Header with filter count and clear button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-medium">Filters</h4>
          {hasActiveFilters && (
            <Badge variant="secondary" className="h-5 text-xs">
              {activeFilterCount} active
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {onRefresh && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRefresh}
              disabled={loading}
              className="h-7 px-2 text-xs"
            >
              <RefreshCw className={cn('h-3 w-3 mr-1', loading && 'animate-spin')} />
              Refresh
            </Button>
          )}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              className="h-7 px-2 text-xs"
            >
              <X className="h-3 w-3 mr-1" />
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Filter Fields Grid */}
      <div className={`grid gap-4 md:grid-cols-${gridColumns}`}>
        {fields.map(renderFilterField)}

        {/* Sort By */}
        {sortFields.length > 0 && (
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground">Sort by</label>
            <Select
              value={filters.sortBy || defaultSortBy || sortFields[0]?.key}
              onValueChange={(value) => updateFilter('sortBy', value)}
            >
              <SelectTrigger className="h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sortFields.map((sort) => (
                  <SelectItem key={sort.key} value={sort.key}>
                    {sort.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Sort Order */}
        {sortFields.length > 0 && (
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground">Order</label>
            <Select
              value={filters.sortOrder || defaultSortOrder}
              onValueChange={(value) => updateFilter('sortOrder', value as SortOrder)}
            >
              <SelectTrigger className="h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="asc">
                  <div className="flex items-center gap-2">
                    <ArrowUpDown className="h-3 w-3" />
                    Ascending
                  </div>
                </SelectItem>
                <SelectItem value="desc">
                  <div className="flex items-center gap-2">
                    <ArrowUpDown className="h-3 w-3 rotate-180" />
                    Descending
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2 pt-2 border-t">
          <span className="text-xs text-muted-foreground">Active filters:</span>
          {activeFilters.map(([key, value]) => {
            const field = fields.find((f) => f.key === key);
            if (!field) return null;

            return (
              <Badge key={key} variant="secondary" className="h-6 text-xs gap-1 pr-1">
                {field.label}: {getFilterDisplayValue(key, value)}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => clearFilter(key)}
                  className="h-4 w-4 p-0 hover:bg-transparent"
                >
                  <X className="h-2 w-2" />
                </Button>
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
}
